'use client';

import { useBreakpoint, useOutsideClick } from '@repo/react-hooks';
import { HighlightKeys } from '@repo/ui/highlight-keys';
import { cn } from '@repo/utils/cn';
import Image from 'next/image';
import React, { type ReactNode, useCallback, useMemo, useState } from 'react';
import GameImagePlaceholder from '@/assets/casino-tile-placeholder.webp';
import { type GameResponseType } from '@/lib/fetch';
import GameCardInfoLayer from '../game-card-layer';

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

type GameCardButton = {
  label?: string;
  props: ButtonProps;
};

type GameCardProps = GameResponseType & {
  onGameCardClick?: () => void;
  primaryButton?: GameCardButton;
  secondaryButton?: GameCardButton;
  favoriteButtonProps?: ButtonProps;
  playingCountRenderer?: () => ReactNode;
  likesCountRenderer?: () => ReactNode;
  className?: string;
  // TODO: Add the following properties to the type
  usersCount?: number;
  highlightedKey?: string;
  isFavoriteGame?: boolean;
  isFavorite?: boolean;
  id?: number;
  balance?: number | null;
};

const shimmer = (width: number, height: number) => `
  <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
    <defs>
      <linearGradient id="shimmer-gradient" x1="0%" y1="0%" x2="100%" y2="0%" gradientUnits="objectBoundingBox">
        <stop offset="0%" stop-color="#2b364a" stop-opacity="1" />
        <stop offset="30%" stop-color="#2f3a50" stop-opacity="1" />
        <stop offset="50%" stop-color="#333f57" stop-opacity="1" />
        <stop offset="70%" stop-color="#2f3a50" stop-opacity="1" />
        <stop offset="100%" stop-color="#2b364a" stop-opacity="1" />
        <animateTransform
          attributeName="gradientTransform"
          type="translate"
          values="-200 0; 200 0; -200 0"
          dur="1.5s"
          repeatCount="indefinite" />
      </linearGradient>
    </defs>
    <rect width="${width}" height="${height}" fill="url(#shimmer-gradient)" />
  </svg>`;

const toBase64 = (str: string) =>
  typeof window === 'undefined' ? Buffer.from(str).toString('base64') : window.btoa(str);

const GameCard = ({
  name,
  provider,
  mainImage,
  onGameCardClick,
  highlightedKey,
  playingCountRenderer,
  likesCountRenderer,
  ...restProps
}: GameCardProps) => {
  const [showInfo, setShowInfo] = useState(false);
  const [imgSrc, setImgSrc] = useState(mainImage?.retina2x?.imageURL || GameImagePlaceholder.src);

  const ref = useOutsideClick(() => setShowInfo(false));

  const { matches: isMobile } = useBreakpoint('sm', 'max');

  const blurDataURL = useMemo(() => {
    return `data:image/svg+xml;base64,${toBase64(
      shimmer(Number(mainImage?.retina2x?.width) || 450, Number(mainImage?.retina2x?.height) || 345)
    )}`;
  }, [mainImage?.retina2x?.width, mainImage?.retina2x?.height]);

  // Memoize image dimensions to prevent recalculation
  const imageDimensions = useMemo(
    () => ({
      width: Number(mainImage?.retina2x?.width) || 450,
      height: Number(mainImage?.retina2x?.height) || 350,
    }),
    [mainImage?.retina2x?.width, mainImage?.retina2x?.height]
  );

  const handleMouseEnter = useCallback(() => {
    if (!isMobile) {
      setShowInfo(true);
    }
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (!isMobile) {
      setShowInfo(false);
    }
  }, [isMobile]);

  const handleClick = useCallback(() => {
    if (isMobile) {
      setShowInfo(true);
    }
    onGameCardClick?.();
  }, [isMobile, onGameCardClick]);

  const handleImageError = useCallback(() => {
    setImgSrc(GameImagePlaceholder.src);
  }, []);

  return (
    <article
      ref={ref}
      data-testid="game-card"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        'group relative size-full overflow-hidden rounded-md transition-shadow duration-200 ease-in-out hover:shadow-sm hover:shadow-[rgba(0,0,0,0.20)]',
        showInfo ? 'vt-atoms-game-card-darker-comp-bg' : 'vt-atoms-game-card-default-comp-bg'
      )}
    >
      {showInfo && <GameCardInfoLayer {...(restProps as any)} />}
      <main className="relative flex w-full flex-col items-center overflow-hidden">
        {typeof playingCountRenderer === 'function' && (
          <div className="relative top-0 z-20 flex w-full items-center justify-center px-1.5 py-1 vt-surface-dark-bg sm:hidden">
            {playingCountRenderer()}
          </div>
        )}
        <Image
          src={imgSrc}
          placeholder="blur"
          blurDataURL={blurDataURL}
          alt={name!}
          loading="lazy"
          width={imageDimensions.width}
          height={imageDimensions.height}
          onError={handleImageError}
          className={cn(
            'size-full h-[var(--vt-game-card-height)] cursor-pointer rounded-t-sm object-cover object-center transition-transform duration-200 ease-in-out sm:h-[var(--vt-game-card-height-sm)]',
            showInfo ? 'scale-110' : 'scale-100'
          )}
        />
      </main>
      <footer
        className={cn('relative flex w-auto cursor-pointer px-[8px] py-[6px] vt-surface-lighter-bg')}
        data-testid="game-card-footer"
      >
        <div className="flex w-full flex-col justify-between space-y-0.5 truncate">
          <div className="flex items-center justify-between space-x-1">
            <span className="block truncate font-bold uppercase vt-font-highlight-font-family vt-text-size-small vt-atoms-game-card-light-text sm:vt-text-size-small">
              <HighlightKeys
                text={name}
                highlight={highlightedKey}
              />
            </span>
            {typeof likesCountRenderer === 'function' && <div className="hidden sm:block">{likesCountRenderer()}</div>}
          </div>
          <div className="flex items-center justify-between space-x-1">
            <span className="block truncate uppercase vt-font-highlight-font-family vt-text-size-smaller vt-atoms-game-card-dark-text sm:vt-text-size-smaller">
              <HighlightKeys
                text={provider}
                highlight={highlightedKey}
              />
            </span>
            {typeof playingCountRenderer === 'function' && (
              <div className="hidden sm:block">{playingCountRenderer()}</div>
            )}
            {typeof likesCountRenderer === 'function' && <div className="block sm:hidden">{likesCountRenderer()}</div>}
          </div>
        </div>
      </footer>
    </article>
  );
};

// Only re-render if the core game data changes
export default React.memo(GameCard, (prevProps, nextProps) => {
  return (
    // Handle balance change and other props
    prevProps.balance === nextProps.balance &&
    prevProps.name === nextProps.name &&
    prevProps.provider === nextProps.provider &&
    prevProps.highlightedKey === nextProps.highlightedKey &&
    prevProps.isFavorite === nextProps.isFavorite &&
    prevProps.isFavoriteGame === nextProps.isFavoriteGame &&
    prevProps.usersCount === nextProps.usersCount &&
    prevProps.id === nextProps.id
  );
});
